import React, { createRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useEditorState, EditorContent } from '@tiptap/react';
import mediaTypes from 'enums/mediaTypes';
import { uploadMedia } from 'actions/media';
import { makeStyles } from 'styles/util';

import { handleResponse } from 'actions/actionHelpers';
import Box from '@material-ui/core/Box';
import Dropzone from 'react-dropzone';

const useStyles = makeStyles(({ theme }) => ({
    editor: {
        border: 'thin solid rgba(0, 0, 0, 0.23)',
        borderRadius: theme.spacing(0.5),
        padding: theme.spacing(0, 2),
        '&:hover': {
            border: 'thin solid rgba(0, 0, 0, 0.23)'
        },
        '& .ProseMirror-focused': {
            outline: 'none'
        }
    }
}));

const Editor = ({ editor }) => {
    const classes = useStyles();
    const dispatch = useDispatch();

    const dropzoneRef = createRef();

    const setLink = useCallback(() => {
        const previousUrl = editor.getAttributes('link').href;
        const url = window.prompt('URL', previousUrl); // eslint-disable-line no-alert

        // cancelled
        if (url === null) {
            return;
        }

        // empty
        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }

        // update link
        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }, []);

    const editorState = useEditorState({
        editor,
        selector: ctx => {
            return {
                isBold: ctx.editor.isActive('bold') ?? false,
                canBold: ctx.editor.can().chain().toggleBold().run() ?? false,
                isItalic: ctx.editor.isActive('italic') ?? false,
                canItalic: ctx.editor.can().chain().toggleItalic().run() ?? false,
                isParagraph: ctx.editor.isActive('paragraph') ?? false,
                isHeading2: ctx.editor.isActive('heading', { level: 2 }) ?? false,
                isHeading3: ctx.editor.isActive('heading', { level: 3 }) ?? false,
                isLink: ctx.editor.isActive('link') ?? false,
                isBulletList: ctx.editor.isActive('bulletList') ?? false,
                isOrderedList: ctx.editor.isActive('orderedList') ?? false,
                canUndo: ctx.editor.can().chain().undo().run() ?? false,
                canRedo: ctx.editor.can().chain().redo().run() ?? false,
            };
        },
    });

    const handleInsertImageButtonClick = () => {
        if (dropzoneRef.current) {
            dropzoneRef.current.open();
        }
    };

    const handleDropImage = acceptedFiles => {
        acceptedFiles.forEach(file => {
            dispatch(uploadMedia(file, mediaTypes.tiptapEditorImage.key))
                .then(handleResponse(
                    response => {
                        const json = response.payload;
                        const mediaUrl = json.location;
                        if (json.success && mediaUrl) {
                            editor.chain().focus().setImage({ src: mediaUrl }).run();
                        }
                    }
                ));
        });
    };

    return (
        <>
            <div className="control-group">
                <div className="button-group">
                    <button
                        type="button"
                        onClick={handleInsertImageButtonClick}
                    >
                        Insert image
                    </button>
                    <button
                        type="button"
                        onClick={() => editor.chain().focus().toggleBold().run()}
                        disabled={!editorState.canBold}
                        className={editorState.isBold ? 'is-active' : ''}
                    >
                        Bold
                    </button>
                    <button
                        type="button"
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                        disabled={!editorState.canItalic}
                        className={editorState.isItalic ? 'is-active' : ''}
                    >
                        Italic
                    </button>
                    <button
                        type="button"
                        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                        className={editorState.isHeading2 ? 'is-active' : ''}
                    >
                        H2
                    </button>
                    <button
                        type="button"
                        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                        className={editorState.isHeading3 ? 'is-active' : ''}
                    >
                        H3
                    </button>
                    <button
                        type="button"
                        onClick={setLink}
                        className={editorState.isLink ? 'is-active' : ''}
                    >
                        Set link
                    </button>
                    <button
                        type="button"
                        onClick={() => editor.chain().focus().unsetLink().run()}
                        disabled={!editorState.isLink}
                    >
                        Unset link
                    </button>
                    <button
                        type="button"
                        onClick={() => editor.chain().focus().toggleBulletList().run()}
                        className={editorState.isBulletList ? 'is-active' : ''}
                    >
                        Bullet list
                    </button>
                    <button
                        type="button"
                        onClick={() => editor.chain().focus().toggleOrderedList().run()}
                        className={editorState.isOrderedList ? 'is-active' : ''}
                    >
                        Ordered list
                    </button>
                    <button
                        type="button"
                        onClick={() => editor.chain().focus().undo().run()}
                        disabled={!editorState.canUndo}
                    >
                        Undo
                    </button>
                    <button
                        type="button"
                        onClick={() => editor.chain().focus().redo().run()}
                        disabled={!editorState.canRedo}
                    >
                        Redo
                    </button>
                </div>
            </div>
            <Box className={classes.editor}>
                <EditorContent editor={editor} />
            </Box>
            <Dropzone
                ref={dropzoneRef}
                accept={mediaTypes.tiptapEditorImage.accept.join(', ')}
                maxSize={16000000}
                maxFiles={1}
                onDrop={handleDropImage}
            >
                {({ getRootProps, getInputProps }) => (
                    <Box {...getRootProps()} component="div">
                        <input {...getInputProps()} />
                    </Box>
                )}
            </Dropzone>
        </>
    );
};

Editor.propTypes = {
    editor: PropTypes.object.isRequired
};

export default Editor;
